import type React from 'react'
import { type ChangeEvent, type ChangeEventHandler, useEffect, useState } from 'react'

import { formatBytes } from 'commons/utils/format'
import { truncateString } from 'commons/utils/string'
import { Icon, Info, Label, SlimButton } from 'components'
import { theme } from 'styles/theme/hav-theme'

import { FieldStyled, FormControlStyled, WrapIconStyled, WrapLabelStyled } from './styles'

export type DSUploadButtonType =
  | 'hav-master-field-input'
  | 'hav-text-field-input'
  | 'hav-search-field-input'

export type OnChangeUpload = ChangeEventHandler<HTMLInputElement>

export interface UploadButtonProps {
  name?: string
  id?: string
  accept?: string
  value: File
  label?: string
  fileName?: string
  disabled?: boolean
  info?: string
  onClear?: () => void
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange: OnChangeUpload
  addorment?: boolean
  error?: string | null
  errorOutline?: boolean
  required?: boolean
  width?: number
  truncateName?: number
  rows?: number
  placeholder?: string
  onClick?: () => void
  padding?: string | number
}

export const UploadButton: React.FC<UploadButtonProps> = ({
  name = undefined,
  id,
  value,
  accept = '',
  placeholder = '',
  label = '',
  fileName,
  info,
  onChange,
  truncateName = 30,
  onClear = () => {
    return
  },
  onClick = () => {
    return
  },
  required = false,
  disabled = false,
  width,
  errorOutline,
  padding,
}) => {
  const [fileTitle, setFileTitle] = useState('')

  useEffect(() => {
    if (value?.name) {
      setFileTitle(value.name)
    } else {
      setFileTitle('')
    }

    if (fileName) {
      setFileTitle(fileName)
    }
  }, [value, fileName])

  const handleOnchange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e) {
      onChange(e)
    }
  }

  const handleFile = () => {
    if (fileTitle) {
      const fileSize = value ? `(${formatBytes(value.size, 0)})` : null

      return fileSize
        ? `${truncateString(fileTitle, truncateName)} ${fileSize}`
        : truncateString(fileTitle, truncateName)
    }

    return placeholder
  }

  return (
    <>
      {label && (
        <WrapLabelStyled hasInfo={info}>
          <Label htmlFor={name} id="LabelInputForm" required={required}>
            {label}
          </Label>
          {info && <Info>{info}</Info>}
        </WrapLabelStyled>
      )}
      <FormControlStyled {...(width && { width })}>
        <FieldStyled
          disabled={disabled}
          errorOutline={errorOutline}
          id="upload-container"
          padding={padding}
        >
          <input
            accept={accept}
            disabled={disabled}
            id={id}
            name={name}
            onChange={handleOnchange}
            required={required}
            type="file"
          />

          <WrapIconStyled
            disabled={disabled}
            justifyContent="space-between"
            onClick={disabled ? () => 0 : onClick}
          >
            <label htmlFor={id}>
              <WrapIconStyled justifyContent="center">
                <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                <span>{handleFile()}</span>
                {required && <span id="asterisk">*</span>}
              </WrapIconStyled>
            </label>
            {fileTitle && (
              <SlimButton icon={<Icon icon="trash" size="20" />} onClick={onClear} variant="icon" />
            )}
          </WrapIconStyled>
        </FieldStyled>
      </FormControlStyled>
    </>
  )
}
